<script setup lang="ts">
  import LogicalOperatorSelect from '@/components/form-wizard/LogicalOperatorSelect.vue'
  import Predicates from '@/components/form-wizard/Predicates.vue'
  import FlowPathInput from '@/components/form/FlowPathInput.vue'
  import { useFlowConfigStore } from '@/store/flow-config.store'
  import { useFlowStore } from '@/store/flow.store'
  import {
    BORDER_SETTINGS,
    ChevronDown,
    ChevronUp,
    DropDown,
    FormFieldContainer,
    InputField,
    Reorder,
    SECTION_TYPE,
    SwitchButton,
    UiButton,
    useXPathAction,
    type BorderSetting,
    type FlowPage,
    type FlowSummary,
    type SectionType,
    type SummarySection,
  } from '@logistiek-zonder-papier/lzp-library'
  import { onMounted, ref } from 'vue'
  import ColorPicker from '@/components/form-wizard/ColorPicker.vue'
  const xPathAction = useXPathAction()

  const field = defineModel<FlowSummary>({ required: true })
  const flowStore = useFlowStore()
  const flowConfigStore = useFlowConfigStore()

  const showAdvanced = ref<number[]>([])

  function getArrayContext(section: SummarySection) {
    if (section.type !== 'array' || section.array == null) return undefined

    const array = xPathAction.getValue(section.array)

    if (!Array.isArray(array)) return undefined

    return array[0]
  }

  function addSection() {
    field.value.sections.push({
      id: undefined,
      type: 'text',
      label: '',
      labelSeparateLine: false,
      array: '',
      value: '',
      border: 'none',
      editValuePageId: null,
      editValueButtonColor: '',
      editValueTextColor: '',
      order: field.value.sections.length,
      predicateCombinator: 'AND',
      predicates: [],
    })
  }

  function deleteSection(index: number) {
    field.value.sections.splice(index, 1)
  }

  onMounted(() => {
    field.value.sections = field.value.sections.sort((lhs, rhs) => lhs.order - rhs.order)
  })

  function toggleAdvanced(sectionIndex: number) {
    const index = showAdvanced.value.indexOf(sectionIndex)
    if (index === -1) {
      showAdvanced.value.push(sectionIndex)
    } else {
      showAdvanced.value.splice(index, 1)
    }
  }
</script>

<template>
  <div class="field-config-container">
    <FormFieldContainer label="Sections">
      <template #action-button>
        <UiButton
          label="Add section"
          variant="prominent"
          size="small"
          icon="plus"
          @click="addSection"
        />
      </template>
      <div class="repeatable">
        <div v-for="(section, index) in field.sections" :key="index" class="section">
          <div class="section-row">
            <FormFieldContainer label="Section Type" class="type">
              <template #action-button>
                <Reorder
                  :modelValue="field.sections"
                  :index="index"
                  @update:modelValue="
                    (value: SummarySection[]) => {
                      field.sections = value.map((field, index) => ({ ...field, order: index }))
                    }
                  "
                />
                <UiButton
                  icon="delete"
                  size="small"
                  variant="negative"
                  @click="deleteSection(index)"
                />
              </template>
              <DropDown
                idKey="key"
                :modelValue="{ key: section.type }"
                @update:modelValue="
                  async (val: { key: SectionType }) => {
                    if (val.key === section.type) return
                    section.type = val.key
                  }
                "
                required
                :data="
                  SECTION_TYPE.map(type => ({
                    key: type,
                  }))
                "
              >
                <template #item="{ item }">
                  {{ item.key }}
                </template>
              </DropDown>
            </FormFieldContainer>
          </div>

          <FlowPathInput
            v-if="section.type !== 'lineBreak'"
            label="Label"
            :optional="false"
            placeholder="Literal Label or ${xpathvalue}"
            v-model="section.label"
            type="text"
            :validate-paths="true"
          />
          <FormFieldContainer v-if="section.type !== 'lineBreak'" label="Label op eigen regel">
            <SwitchButton v-model="section.labelSeparateLine" size="sm" />
          </FormFieldContainer>

          <FlowPathInput
            label="Array Root"
            v-if="section.type === 'array'"
            :optional="false"
            placeholder="${xpathvalue} to the array in context"
            v-model="section.array"
            type="text"
            :validate-paths="true"
          />

          <FlowPathInput
            v-if="section.type !== 'lineBreak'"
            label="Value"
            :optional="false"
            placeholder="${xpathvalue} to value in context or in element of array"
            v-model="section.value"
            type="textarea"
            :validate-paths="true"
            :context="getArrayContext(section)"
          />

          <span @click="toggleAdvanced(index)" class="advanced-label">
            Advanced Settings
            <component :is="showAdvanced.includes(index) ? ChevronDown : ChevronUp"></component>
          </span>

          <div v-if="showAdvanced.includes(index)" class="section">
            <div class="inline">
              <DropDown
                :modelValue="
                  flowConfigStore.config?.pages.find(page => page.id === section.editValuePageId)
                "
                :data="flowConfigStore.config?.pages ?? []"
                @update:modelValue="(page: FlowPage) => (section.editValuePageId = page.id)"
              >
                <template #item="{ item }"> {{ item.order + 1 }} - {{ item.name }} </template>
              </DropDown>
              <UiButton
                icon="delete"
                size="small"
                variant="negative"
                @click="section.editValuePageId = null"
              />
            </div>

            <template v-if="section.editValuePageId">
              <FormFieldContainer label="Button Colour" class="color-field">
                <div class="inline">
                  <ColorPicker v-model="section.editValueButtonColor" />
                  <InputField type="text" v-model="section.editValueButtonColor" />
                </div>
              </FormFieldContainer>

              <FormFieldContainer label="Text Colour" class="color-field">
                <div class="inline">
                  <ColorPicker v-model="section.editValueTextColor" />
                  <InputField type="text" v-model="section.editValueTextColor" />
                </div>
              </FormFieldContainer>
            </template>

            <DropDown
              label="Border"
              :modelValue="{ value: section.border }"
              idKey="value"
              :data="
                BORDER_SETTINGS.map(value => ({
                  value,
                }))
              "
              @update:modelValue="
                (value: { value: BorderSetting }) => (section.border = value.value)
              "
            >
              <template #item="{ item }">{{ item.value ?? 'Selecteer een optie' }}</template>
            </DropDown>

            <LogicalOperatorSelect v-model="section.predicateCombinator" label="Filter Logic" />

            <Predicates v-model="section.predicates" :context="flowStore.flowData" />
          </div>
        </div>
      </div>
    </FormFieldContainer>
  </div>
</template>

<style scoped>
  .inline {
    width: 100%;
    display: flex;
    gap: var(--spacing-s);
    align-items: center;
    justify-content: space-between;
  }

  .colour-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-s);
  }

  .field-config-container {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .repeatable {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-s);
  }

  .section {
    display: flex;
    flex-direction: column;
    padding: var(--spacing-xs);
    border-radius: var(--components-form-field-corners);
    border: 1px solid var(--colours-grey-3);
    box-shadow: 1px 1px 1px 1px var(--colours-grey-3);
  }

  .section-row {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: var(--spacing-xs);
  }

  .type {
    flex: 1 0 0;
  }

  .advanced-label {
    user-select: none;
    cursor: pointer;
    padding-bottom: var(--spacing-xs);
    color: var(--colours-grey-5);
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
  }
</style>
